import type * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

/**
 * The model loaders, set up once and shared (docs/ASSETS.md, "Compressed models"). Every file in public/models is
 * built by scripts/compress-models.mjs: textures KTX2 (Basis Universal), transcoded in a worker to what the GPU
 * takes (ASTC on an iPhone, ETC2 / BC elsewhere, RGBA8 as the last resort) so they stay compressed in GPU
 * memory; geometry meshopt-compressed (EXT_meshopt_compression) with 8-bit normals and 16-bit UVs
 * (KHR_mesh_quantization). The transcoder is served from public/basis/ (no CDN).
 *
 * The renderer registers itself (createRenderer) so the KTX2 loader can ask which formats the GPU has. Loads
 * before that (tests, no WebGL) fail, and every model keeps its procedural fallback, as when a file is missing.
 */
const BASE = import.meta.env?.BASE_URL ?? './';
let ktx2: KTX2Loader | null = null;
let gltf: GLTFLoader | null = null;
let renderer: THREE.WebGLRenderer | null = null;
/** The renderer whose formats the KTX2 loader was last told about. */
let detectedFor: THREE.WebGLRenderer | null = null;

/** Called once by createRenderer. */
export function registerKtx2Renderer(r: THREE.WebGLRenderer): void {
  renderer = r;
}

/**
 * Asks the GPU which formats it takes (detectSupport) when the first file needs to know, not when the loader is
 * made: right after the WebGL context is created those queries wait on the GPU process (≈ 0.1 s on the dev box),
 * and the boot makes the loader then to start its downloads early (world/worldSystem.ts; docs/PERF.md "Load time").
 */
class LateSupportKtx2Loader extends KTX2Loader {
  override load(...args: Parameters<KTX2Loader['load']>): void {
    this.support();
    super.load(...args);
  }

  override parse(...args: Parameters<KTX2Loader['parse']>): void {
    this.support();
    super.parse(...args);
  }

  private support(): void {
    if (!renderer || detectedFor === renderer) return;
    this.detectSupport(renderer);
    detectedFor = renderer;
  }
}

/** The shared KTX2 loader (the detailed cars' atlases load through it directly). */
export function ktx2Loader(): KTX2Loader {
  // Two workers: the textures arrive a few at a time, and a phone has the main thread to feed.
  ktx2 ??= new LateSupportKtx2Loader().setTranscoderPath(`${BASE}basis/`).setWorkerLimit(2);
  return ktx2;
}

/** The shared glTF loader: meshopt geometry and KTX2 textures. */
export function modelLoader(): GLTFLoader {
  gltf ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).setKTX2Loader(ktx2Loader());
  return gltf;
}
